import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Download, X, Bell, Smartphone } from "lucide-react";
import usePWA from "@/hooks/usePWA";
import { toast } from "sonner";

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const { 
    isInstallable, 
    isInstalled, 
    installApp, 
    notificationPermission,
    requestNotificationPermission,
    showLocalNotification
  } = usePWA();

  useEffect(() => {
    // Show install prompt after 30 seconds if installable
    if (isInstallable && !isInstalled) {
      const timer = setTimeout(() => {
        const dismissed = localStorage.getItem('pwa-prompt-dismissed');
        if (!dismissed) {
          setShowPrompt(true);
        }
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [isInstallable, isInstalled]);

  useEffect(() => {
    // Show notification prompt after install or after 60 seconds
    if (notificationPermission === 'default') {
      const timer = setTimeout(() => {
        const dismissed = localStorage.getItem('notification-prompt-dismissed');
        if (!dismissed) {
          setShowNotificationPrompt(true);
        }
      }, 60000);
      return () => clearTimeout(timer);
    }
  }, [notificationPermission]);

  const handleInstall = async () => {
    const installed = await installApp();
    if (installed) {
      toast.success("App installata con successo!");
      setShowPrompt(false);
    }
  };

  const handleDismissInstall = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  const handleEnableNotifications = async () => {
    const permission = await requestNotificationPermission();
    if (permission === 'granted') {
      toast.success("Notifiche attivate!");
      // Send test notification
      await showLocalNotification("Sagre Italia", {
        body: "Le notifiche sono attive! Ti avviseremo delle nuove sagre.",
        data: { url: '/' }
      });
    } else if (permission === 'denied') {
      toast.error("Notifiche bloccate. Puoi attivarle dalle impostazioni del browser.");
    }
    setShowNotificationPrompt(false);
  };

  const handleDismissNotification = () => {
    setShowNotificationPrompt(false);
    localStorage.setItem('notification-prompt-dismissed', 'true');
  };

  return (
    <>
      {/* Install Prompt */}
      {showPrompt && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up" data-testid="pwa-install-prompt">
          <Card className="border-0 shadow-floating bg-white">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1">Installa Sagre Italia</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Aggiungi l'app alla schermata home per un accesso rapido e funzionalità offline.
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={handleInstall}
                      className="rounded-full gap-1"
                      data-testid="pwa-install-btn"
                    >
                      <Download className="w-3 h-3" />
                      Installa
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={handleDismissInstall}
                      className="rounded-full"
                    >
                      Non ora
                    </Button>
                  </div>
                </div>
                <button 
                  onClick={handleDismissInstall}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Notification Prompt */}
      {showNotificationPrompt && !showPrompt && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up" data-testid="notification-prompt">
          <Card className="border-0 shadow-floating bg-white">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#f2c200]/20 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-6 h-6 text-[#f2c200]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1">Attiva le notifiche</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Ricevi avvisi sulle nuove sagre nella tua zona e non perderti nessun evento!
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={handleEnableNotifications}
                      className="rounded-full gap-1 bg-[#f2c200] hover:bg-[#d9ad00] text-foreground"
                      data-testid="enable-notifications-btn"
                    >
                      <Bell className="w-3 h-3" />
                      Attiva
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={handleDismissNotification}
                      className="rounded-full"
                    >
                      Non ora
                    </Button>
                  </div>
                </div>
                <button 
                  onClick={handleDismissNotification}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
